import { format, parseISO } from 'date-fns'

import { EMPTY_LIST, NOTIFICATION_TYPE } from '../../../../../constants/common'
import { DATE_FULL_FORMAT_FROM_TIME } from '../../../../../constants/date'
import {
  APPLICANT_STAGE,
  getRentalPortalApplicantIdentityLabel,
} from '../../../../RentalPortalApplicant'
import { APPLICANT_DECISION_ACTION } from '../..'
import { getIsApplicantPoolStage } from '../RentalPortalApplicantProfileTabOverview.utils'

import {
  APPLICANT_DECISION_ACTION_LABEL_KEY,
  APPLICANT_DECISION_BLOCKED_TOOLTIP_KEY,
  APPLICANT_DECISION_CONFIRM_TEXT_KEY,
  APPLICANT_DECISION_CONFIRM_TITLE_KEY,
  APPLICANT_DECISION_RESELECT_NOTIFICATION_ID,
  APPLICANT_DECISION_SAME_USER_AS_SELECTOR_REASON,
  APPLICANT_DECISION_SAVE_ERROR_NOTIFICATION_ID,
  APPLICANT_DECISION_STATUS_TEXT_COLOR,
  APPLICANT_DECISION_TRANSITION_KEY,
} from './ApplicantDecisionPanel.constants'

function getApplicantDecisionTimeText(value) {
  return value ? format(parseISO(value), DATE_FULL_FORMAT_FROM_TIME) : null
}

function getIsApplicantFourEyesRequired(applicantData) {
  return Boolean(applicantData?.policy?.four_eyes_required)
}

function getIsApplicantSelectionRejected(applicantData) {
  return Boolean(applicantData?.rejected_at)
}

function getIsApplicantSelectedStage(applicantData) {
  return applicantData?.stage === APPLICANT_STAGE.selected
}

function getIsApplicantDecisionAwaiting(applicantData) {
  return Boolean(
    getIsApplicantSelectedStage(applicantData) &&
    !applicantData.approved_by &&
    !getIsApplicantSelectionRejected(applicantData)
  )
}

function toApplicantDecisionSessionValue(currentUser) {
  return currentUser
    ? {
        avatar: currentUser.avatar,
        avatarColor: currentUser.avatar_color,
        email: currentUser.email,
        fullname: currentUser.fullname,
        id: currentUser.id,
      }
    : null
}

function getApplicantDecisionBlockedTooltipKey({ action, applicantData }) {
  const transition = applicantData.available_transitions?.find(
    ({ key }) => key === APPLICANT_DECISION_TRANSITION_KEY[action]
  )

  return transition && !transition.permitted
    ? APPLICANT_DECISION_BLOCKED_TOOLTIP_KEY[action]
    : null
}

export function getHasApplicantDecisionPanel(applicantData) {
  return Boolean(
    applicantData?.stage && !getIsApplicantPoolStage(applicantData)
  )
}

export function getApplicantDecisionEligibleCount(applicantData) {
  return applicantData?.eligible_reviewer_count ?? null
}

export function getApplicantDecisionReviewers(applicantData) {
  return (
    (getIsApplicantDecisionAwaiting(applicantData) &&
      getIsApplicantFourEyesRequired(applicantData) &&
      applicantData.eligible_reviewers) ||
    EMPTY_LIST
  )
}

export function toApplicantDecisionUserValue(user) {
  return user
    ? {
        archived: user.is_archived,
        avatarColor: user.avatar_color,
        companyPostName: user.job_title,
        email: user.email,
        fullname: user.full_name,
        id: user.id,
      }
    : null
}

export function getIsApplicantDecisionSelfApproval({
  applicantData,
  currentUserId,
}) {
  return Boolean(
    getIsApplicantFourEyesRequired(applicantData) &&
    currentUserId != null &&
    applicantData?.selected_by_user_id === currentUserId
  )
}

export function getIsApplicantDecisionSelectAction(action) {
  return action.key === APPLICANT_DECISION_ACTION.select
}

export function getApplicantDecisionStepActions(actions) {
  return {
    approvalActions: actions.filter(
      action => !getIsApplicantDecisionSelectAction(action)
    ),
    selectActions: actions.filter(action =>
      getIsApplicantDecisionSelectAction(action)
    ),
  }
}

export function getIsApplicantScreeningStage(applicantData) {
  return applicantData?.stage === APPLICANT_STAGE.screening
}

export function getApplicantDecisionActions({
  applicantData,
  currentUserId,
  isPreview,
}) {
  if (isPreview) {
    return EMPTY_LIST
  }

  if (getIsApplicantScreeningStage(applicantData)) {
    return applicantData.permissions?.can_select
      ? [
          {
            key: APPLICANT_DECISION_ACTION.select,
            tooltipKey: getApplicantDecisionBlockedTooltipKey({
              action: APPLICANT_DECISION_ACTION.select,
              applicantData,
            }),
          },
        ]
      : EMPTY_LIST
  }

  if (
    !getIsApplicantSelectedStage(applicantData) ||
    !applicantData.permissions?.can_approve
  ) {
    return EMPTY_LIST
  }

  const isSelfApproval = getIsApplicantDecisionSelfApproval({
    applicantData,
    currentUserId,
  })

  return [
    APPLICANT_DECISION_ACTION.approve,
    APPLICANT_DECISION_ACTION.rejectSelection,
  ].map(action => ({
    key: action,
    tooltipKey: isSelfApproval
      ? APPLICANT_DECISION_BLOCKED_TOOLTIP_KEY[action]
      : getApplicantDecisionBlockedTooltipKey({ action, applicantData }),
  }))
}

export function getApplicantDecisionAwaitingText({ applicantData, t }) {
  return getIsApplicantDecisionAwaiting(applicantData) &&
    getIsApplicantFourEyesRequired(applicantData)
    ? t('RentalPortal.ApplicantDecisionAwaitingReviewerKey', {
        count: getApplicantDecisionEligibleCount(applicantData) ?? 0,
      })
    : null
}

export function getApplicantDecisionEscalationKey(applicantData) {
  return getIsApplicantDecisionAwaiting(applicantData) &&
    getIsApplicantFourEyesRequired(applicantData) &&
    getApplicantDecisionEligibleCount(applicantData) === 0
    ? 'RentalPortal.ApplicantDecisionEscalationHintKey'
    : null
}

export function getApplicantDecisionSelectedRow({
  applicantData,
  currentUser,
  t,
}) {
  const label = t('RentalPortal.ApplicantDecisionSelectedByLedgerKey')

  if (applicantData?.is_selected && applicantData.selected_by) {
    return {
      label,
      note: applicantData.selection_note
        ? {
            label: t('RentalPortal.ApplicantDecisionSelectionNoteLabelKey'),
            text: applicantData.selection_note,
          }
        : null,
      statusProps: {
        text: t('RentalPortal.ApplicantDecisionStatusSelectedKey'),
        textColor: APPLICANT_DECISION_STATUS_TEXT_COLOR.selected,
      },
      text: getApplicantDecisionTimeText(applicantData.selected_at),
      user: toApplicantDecisionUserValue(applicantData.selected_by),
    }
  }

  return applicantData?.permissions?.can_select && currentUser
    ? {
        isMuted: true,
        label,
        text: t('RentalPortal.ApplicantDecisionSelectedByProspectiveKey'),
        user: toApplicantDecisionSessionValue(currentUser),
      }
    : {
        isMuted: true,
        label,
        text: t('RentalPortal.ApplicantDecisionSelectedByEmptyKey'),
      }
}

export function getApplicantDecisionApprovalsRow({ applicantData, t }) {
  return {
    hint: getIsApplicantFourEyesRequired(applicantData)
      ? t('RentalPortal.ApplicantDecisionFourEyesRuleKey', {
          count: getApplicantDecisionEligibleCount(applicantData) ?? 0,
        })
      : t('RentalPortal.ApplicantDecisionNoSecondApproverKey'),
    label: t('RentalPortal.ApplicantDecisionApprovalsLabelKey'),
  }
}

export function getApplicantDecisionApprovalPeopleRows({ applicantData, t }) {
  if (applicantData?.approved_by) {
    return [
      {
        id: applicantData.approved_by.id,
        note: applicantData.approval_note
          ? {
              label: t('RentalPortal.ApplicantDecisionReviewerNoteLabelKey'),
              text: applicantData.approval_note,
            }
          : null,
        statusProps: {
          text: t('RentalPortal.ApplicantDecisionStatusApprovedKey'),
          textColor: APPLICANT_DECISION_STATUS_TEXT_COLOR.approved,
        },
        text: getApplicantDecisionTimeText(applicantData.approved_at),
        user: toApplicantDecisionUserValue(applicantData.approved_by),
      },
    ]
  }

  if (getIsApplicantSelectionRejected(applicantData)) {
    return [
      {
        id: applicantData.rejected_by?.id,
        note: applicantData.rejection_reason
          ? {
              label: t('RentalPortal.ApplicantDecisionRejectReasonLabelKey'),
              text: applicantData.rejection_reason,
            }
          : null,
        statusProps: {
          text: t('RentalPortal.ApplicantDecisionStatusRejectedKey'),
          textColor: APPLICANT_DECISION_STATUS_TEXT_COLOR.rejected,
        },
        text: getApplicantDecisionTimeText(applicantData.rejected_at),
        user: toApplicantDecisionUserValue(applicantData.rejected_by),
      },
    ]
  }

  const awaitingStatusProps = {
    text: t('RentalPortal.ApplicantDecisionStatusAwaitingKey'),
    textColor: APPLICANT_DECISION_STATUS_TEXT_COLOR.awaiting,
  }

  return getIsApplicantFourEyesRequired(applicantData)
    ? getApplicantDecisionReviewers(applicantData).map(reviewer => ({
        id: reviewer.id,
        isMuted: true,
        statusProps: awaitingStatusProps,
        user: toApplicantDecisionUserValue(reviewer),
      }))
    : [
        {
          id: applicantData?.selected_by?.id,
          isMuted: true,
          statusProps: awaitingStatusProps,
          user: toApplicantDecisionUserValue(applicantData?.selected_by),
        },
      ]
}

export function getApplicantDecisionConfirmProps({ action, applicantData, t }) {
  return {
    description: t(APPLICANT_DECISION_CONFIRM_TEXT_KEY[action], {
      applicant: getRentalPortalApplicantIdentityLabel({
        applicant: applicantData,
        t,
      }),
    }),
    submitText: t(APPLICANT_DECISION_ACTION_LABEL_KEY[action]),
    title: t(APPLICANT_DECISION_CONFIRM_TITLE_KEY[action]),
  }
}

export function getIsApplicantDecisionReasonRequired(action) {
  return action === APPLICANT_DECISION_ACTION.rejectSelection
}

export function getIsApplicantDecisionUserIconsShown() {
  return false
}

export function getApplicantDecisionModalNotifications({
  action,
  hasError,
  t,
}) {
  return [
    action === APPLICANT_DECISION_ACTION.select && {
      id: APPLICANT_DECISION_RESELECT_NOTIFICATION_ID,
      text: t('RentalPortal.ApplicantDecisionReselectHintKey'),
      type: NOTIFICATION_TYPE.info,
    },
    hasError && {
      id: APPLICANT_DECISION_SAVE_ERROR_NOTIFICATION_ID,
      text: t('RentalPortal.ApplicantDecisionSaveErrorKey'),
      type: NOTIFICATION_TYPE.error,
    },
  ].filter(Boolean)
}

export function getApplicantDecisionConflictKey(error) {
  return error.response.data?.meta?.reason ===
    APPLICANT_DECISION_SAME_USER_AS_SELECTOR_REASON
    ? 'RentalPortal.ApplicantDecisionSelectorBlockedTooltipKey'
    : 'RentalPortal.ApplicantDecisionConflictKey'
}
